import { ButtonBase } from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";

import { Button } from "@/components/Button/Button";
import { IconBalloon } from "@/components/Icons";
import { TripTourDrawer } from "@/features/trips/components/bookingFlow/tripDetails/TripTourDrawer";
import type { BookingData, TripDetails } from "@/features/trips/types/types";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

type TripTourCardProps = {
  trip: TripDetails;
  source: "summary" | "details";
};

export function TripTourCard({ trip, source }: TripTourCardProps) {
  const t = useTranslations();
  const { setValue } = useFormContext<BookingData>();

  const [isTourDetailsOpen, setTourDetailsOpen] = useState(false);

  const isTripTourAdded = useWatch<BookingData, "isTripTourAdded">({
    name: "isTripTourAdded",
  });

  return (
    <>
      <div>
        <ButtonBase
          type="button"
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            borderRadius: "24px",
            backgroundColor: "white",
            p: "16px",
          }}
          onClick={() => {
            rudderAnalytics.track(
              TripTrackingEvents.USER_VIEWED_TRIP_OPTIONAL_TOURS,
              {
                source,
                trip_id: trip.id,
                trip_name: trip.name,
                price: trip.tourPrice ?? 0,
              },
            );

            setTourDetailsOpen(true);
          }}
        >
          <div className="flex items-center gap-2">
            <IconBalloon />

            <div className="text-start">
              <p className="mb-1 font-medium text-text-primary">
                {t("trips.tours.card.title")}
              </p>

              <p className="text-sm font-medium text-core-rahal-600">
                {isTripTourAdded
                  ? t("trips.tours.card.included")
                  : t("trips.tours.card.priceFrom", {
                      price: formatCurrency(trip.tourPrice),
                    })}
              </p>
            </div>
          </div>

          <span className="flex items-center gap-2 text-sm font-bold text-text-interactive">
            {t(`trips.tours.card.${isTripTourAdded ? "details" : "add"}`)}
            {!isTripTourAdded && (
              <PlusIcon weight="bold" className="shrink-0" size={24} />
            )}
          </span>
        </ButtonBase>
      </div>

      <TripTourDrawer
        trip={trip}
        open={isTourDetailsOpen}
        onClose={() => setTourDetailsOpen(false)}
      >
        {isTripTourAdded ? (
          <Button
            fullWidth
            size="large"
            variant="outlined"
            color="error"
            onClick={() => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_REMOVED_TRIP_OPTIONAL_TOURS,
                {
                  source,
                  trip_id: trip.id,
                  trip_name: trip.name,
                  price: trip.tourPrice ?? 0,
                },
              );

              setValue("isTripTourAdded", false);
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.remove")}
          </Button>
        ) : (
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_ADDED_TRIP_OPTIONAL_TOURS,
                {
                  source,
                  trip_id: trip.id,
                  trip_name: trip.name,
                  price: trip.tourPrice ?? 0,
                },
              );

              setValue("isTripTourAdded", true);
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.addTours")}
          </Button>
        )}

        {isTripTourAdded && (
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => {
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.confirm")}
          </Button>
        )}
      </TripTourDrawer>
    </>
  );
}
